// HTTP layer for the marketing-suite backend (the localStorage → API swap).
//
// Every FE-first store (taxes, loyalty, journeys, My Templates, business
// details) keeps its synchronous localStorage reads as a CACHE; this module
// is what their hydrate/write-through calls go through. Auth matches the
// rest of the codebase (Basic + accessToken header); responses use the
// standard { statusCode: 1, responseData: { result } } envelope, unwrapped
// here so stores deal in plain data.

import axios from 'axios';
import API from '../../../config/API';
import { addToLibrary } from './imageLibrary';

const headers = () => ({
  'Content-Type': 'application/json',
  'authorization': 'Basic YmV1YXBwOmJldWFwcA==',
  'accessToken': `${localStorage.getItem('accessToken')}`,
});

const unwrap = (res) => {
  if (res?.data?.statusCode === 1) return res.data.responseData?.result ?? res.data.responseData;
  const message = res?.data?.responseData?.message || res?.data?.error?.responseMessage || 'Request failed';
  throw new Error(message);
};

// Tolerant unwrap for the client-consent endpoints, which may land on either
// the house envelope ({ statusCode:1, responseData:{ result } }) or the leaner
// { status:1, data } shape the BE work was specced against. Returns the inner
// payload regardless; throws with the server reason on a non-ok status.
const unwrapClient = (res) => {
  const d = res?.data;
  if (d?.statusCode === 1) return d.responseData?.result ?? d.responseData ?? {};
  if (d?.status === 1) return d.data ?? {};
  const message = d?.responseData?.message || d?.error?.responseMessage || d?.message || 'Request failed';
  throw new Error(message);
};

const get = (url) => axios.get(url, { headers: headers() }).then(unwrap);
const post = (url, body) => axios.post(url, body || {}, { headers: headers() }).then(unwrap);
const put = (url, body) => axios.put(url, body || {}, { headers: headers() }).then(unwrap);

export const marketingBackend = {
  // ---- taxes (creator-scoped; list returns the whole taxStore shape) ----
  taxList: () => get(API.TAX_LIST),
  taxSave: (tax) => post(API.TAX_SAVE, tax),
  taxDelete: (id) => post(API.TAX_DELETE, { id }),
  taxMode: (calculationMode) => post(API.TAX_MODE, { calculationMode }),
  taxItemOverride: (domain, itemId, taxIds) => post(API.TAX_ITEM_OVERRIDE, { domain, itemId, taxIds }),
  // Master switch + VAT number (docs/tax-money-path.md §1 #9/#10).
  taxSettings: (patch) => post(API.TAX_SETTINGS, patch),
  // Category defaults (keyed by category name) and country presets.
  taxCategories: () => get(API.TAX_CATEGORIES),
  taxCategoryOverride: (domain, categoryName, taxIds) => post(API.TAX_CATEGORY_OVERRIDE, { domain, categoryName, taxIds }),
  taxPresets: () => get(API.TAX_PRESETS),
  // The store that applies to a stylist's sales (the seller of record's).
  taxForSeller: (hairdresserUserId) => get(API.TAX_FOR_SELLER + (hairdresserUserId ? `?hairdresserUserId=${encodeURIComponent(hairdresserUserId)}` : '')),

  // ---- loyalty ----
  loyaltyGet: () => get(API.LOYALTY_SCHEME),
  loyaltySave: (scheme) => post(API.LOYALTY_SCHEME, scheme),
  // honour=true keeps already-earned rewards redeemable; false voids them.
  loyaltyRemove: (honour) => post(API.LOYALTY_SCHEME_REMOVE, { honour }),
  // In-flight rewards on the caller's scheme (for the delete-confirmation modal).
  loyaltySchemeInflight: () => get(API.LOYALTY_SCHEME_INFLIGHT),
  loyaltyStats: () => get(API.LOYALTY_STATS),
  loyaltyOffer: (stylistUserId, offered) => post(API.LOYALTY_ELECTION_OFFER, { stylistUserId, offered }),
  loyaltyJoin: (joined) => post(API.LOYALTY_ELECTION_JOIN, { joined }),
  loyaltyElections: () => get(API.LOYALTY_ELECTION_LIST),
  // Salon-side: a specific client's real loyalty card(s) at this salon (panel/till view).
  loyaltyClientCards: (clientUserId) => post(API.LOYALTY_CLIENT_CARDS, { clientUserId }),
  // A client's loyalty history (earn/redeem/reverse audit trail) for the panel.
  // Paginated: returns { history:[...] (newest first), page, hasMore }.
  loyaltyClientHistory: (clientUserId, schemeId, page = 1, limit = 25) =>
    post(API.LOYALTY_CLIENT_HISTORY, { clientUserId, schemeId, page, limit }),

  // ---- client panel: paginated history tabs ----
  // Each returns a page of rows + { page, hasMore } so the panel can lazy-load
  // page 1 on tab-open and append subsequent pages behind a "Load more" button
  // rather than hauling the whole history down on panel open. All tolerate
  // either envelope ({statusCode:1,responseData} or {status:1,data}) via unwrapClient.

  // Bookings for one client, split by past/upcoming. Shape: { bookings:[...], page, hasMore }.
  clientBookings: (clientId, type, page = 1, limit = 25) =>
    axios.post(API.CLIENT_BOOKINGS, { clientId, type, page, limit }, { headers: headers() }).then(unwrapClient),
  // Product + voucher sales for one client. Shape: { sales:[...], page, hasMore }.
  clientSales: (clientId, page = 1, limit = 25) =>
    axios.post(API.CLIENT_SALES, { clientId, page, limit }, { headers: headers() }).then(unwrapClient),

  // ---- client marketing-consent (client panel Message tab) ----
  // The consent audit trail + current marketable state + suppressions for one
  // client. Shape: { current:{emailMarketingConsent, smsMarketingConsent},
  // history:[...] (newest first), suppressions:[...], page, hasMore }. Both
  // endpoints tolerate either envelope ({statusCode:1,responseData} or
  // {status:1,data}) via unwrapClient.
  clientConsentHistory: (clientId, page = 1, limit = 25) =>
    axios.post(API.CLIENT_CONSENT_HISTORY, { clientId, page, limit }, { headers: headers() }).then(unwrapClient),
  // Marketing messages (campaigns + automations) this client has received.
  // Shape: { messages:[{campaignId, name, subject, channel, sentAt, status,
  // openedAt, clickedAt, bouncedAt}] (newest first), page, hasMore }.
  clientMarketingMessages: (clientId, page = 1, limit = 25) =>
    axios.post(API.CLIENT_MARKETING_MESSAGES, { clientId, page, limit }, { headers: headers() }).then(unwrapClient),

  // ---- journeys ----
  journeyList: () => get(API.JOURNEY_LIST),
  journeySave: (journey) => post(API.JOURNEY_SAVE, journey),
  journeyStatus: (id, status) => post(API.JOURNEY_STATUS, { id, status }),
  journeyDelete: (id) => post(API.JOURNEY_DELETE, { id }),
  journeyRuns: (journeyId, limit) => post(API.JOURNEY_RUNS_LIST, { journeyId, limit }),
  journeyRunsByClient: (clientUserId) => post(API.JOURNEY_RUNS_BY_CLIENT, { clientUserId }),
  journeyRunCancel: (runId) => post(API.JOURNEY_RUNS_CANCEL, { runId }),

  // ---- My Templates ----
  templatesList: () => get(API.MARKETING_TEMPLATES),
  templateSave: (tpl) => post(API.MARKETING_TEMPLATES, tpl),
  templateDelete: (id) => post(API.MARKETING_TEMPLATES_DELETE, { id }),

  // ---- business legal details ----
  businessDetailsGet: () => get(API.MARKETING_BUSINESS_DETAILS),
  businessDetailsSave: (details) => post(API.MARKETING_BUSINESS_DETAILS, details),

  // ---- Bailey's plan memory (per salon; per stylist for the self-employed) ----
  // PUT merges key by key: an entry replaces that plan's, null removes it.
  planMemoryGet: () => get(API.MARKETING_PLAN_MEMORY),
  planMemoryPatch: (patch) => put(API.MARKETING_PLAN_MEMORY, patch),
  // ---- Studio campaigns (campaignStore's server side) ----
  campaignGroupList: (range = {}) => get(API.MARKETING_CAMPAIGN_GROUP + (range.from || range.to ? `?${[range.from && `from=${range.from}`, range.to && `to=${range.to}`].filter(Boolean).join('&')}` : '')),
  campaignGroupSave: (id, campaign) => put(`${API.MARKETING_CAMPAIGN_GROUP}/${encodeURIComponent(id)}`, campaign),
  campaignGroupRemove: (id) => axios.delete(`${API.MARKETING_CAMPAIGN_GROUP}/${encodeURIComponent(id)}`, { headers: headers() }).then(unwrap),
  campaignGroupCopy: (id, salonIds) => post(`${API.MARKETING_CAMPAIGN_GROUP}/${encodeURIComponent(id)}/copy`, { salonIds }),

  // ---- designer image upload (multipart; returns { url }) ----
  uploadImage: (file) => {
    const form = new FormData();
    form.append('image', file);
    return axios.post(API.MARKETING_UPLOAD_IMAGE, form, {
      headers: {
        'authorization': 'Basic YmV1YXBwOmJldWFwcA==',
        'accessToken': `${localStorage.getItem('accessToken')}`,
      },
    }).then(unwrap).then((res) => {
      // Every upload joins the salon's image library for one-tap reuse (the
      // server records it too and replies with its id and size)
      if (res && res.url) addToLibrary({ id: res.id, url: res.url, name: res.name || (file && file.name), width: res.width, height: res.height });
      return res;
    });
  },
};

// A server id is a Mongo ObjectId; FE-generated ids (tax-…, jrn-…, utpl-…,
// loy-…) must never be sent as ids — the BE treats their absence as create.
export const isServerId = (id) => typeof id === 'string' && /^[0-9a-f]{24}$/i.test(id);
